import React, { Fragment, useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Icon,
  IconButton,
  Tooltip,
  Chip,
  Menu,
  MenuItem,
  Divider,
  Card,
  CardContent,
  Grid,
  useTheme,
} from "@mui/material";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import invoiceService from "../../services/invoice.service";
import { EmptyState } from "../../../../components/EmptyState/EmptyState";
import {
  IESDoc,
  TableColumn,
  WithId,
  FilterGroup,
} from "../../../../app.interface";
import { PageHeader } from "../../components/PageHeader/PageHeader";
import { AppTable } from "../../../../components/AppTable";
import { AppSearch } from "../../../../components/AppSearch";
import { useAuth } from "../../../auth/hooks/useAuth";
import {
  PurchaseInvoice,
  InvoiceStatus,
  ListInvoicesRequest,
} from "../../types/invoice.types";
import { getBusinessId } from "../../services/invoice.service";
import { grnService } from "../../services/grn.service";
import { hasPermission } from "../../../auth/utils/permission.utils";

// Status configuration for invoices (aligned with API documentation)
const invoiceStatusConfig: Record<
  InvoiceStatus,
  { label: string; color: any }
> = {
  draft: { label: "Draft", color: "default" },
  pending_approval: { label: "Pending Approval", color: "warning" },
  approved: { label: "Approved", color: "success" },
  paid: { label: "Paid", color: "info" },
};

export const InvoiceList: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const theme = useTheme();

  // Data state
  const [invoices, setInvoices] = useState<PurchaseInvoice[]>([]);
  const [totalItems, setTotalItems] = useState(0);

  // UI state
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState<Record<string, any>>({});
  const [sortField, setSortField] = useState("created_at");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  // Menu state
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuInvoiceId, setMenuInvoiceId] = useState<string | null>(null);

  // Get business ID
  const getBusinessIdForList = () => getBusinessId(user);

  // Table columns
  const columns: TableColumn[] = [
    {
      id: "invoice_number",
      header: "Invoice Number",
      field: "invoice_number",
      type: "text",
      sortable: true,
    },
    {
      id: "vendor_name",
      header: "Vendor",
      field: "vendor_details.vendor_name",
      type: "text",
      sortable: true,
      accessor: (row: any) =>
        row.vendor_details?.vendor_name ||
        row.vendor_details?.supplier_name ||
        "-",
    },
    {
      id: "grn_number",
      header: "GRN Number",
      field: "grn_references.0.grn_number",
      type: "text",
      sortable: false,
      accessor: (row: any) => row.grn_references?.[0]?.grn_number || "-",
    },
    {
      id: "po_number",
      header: "PO Number",
      field: "grn_references.0.po_number",
      type: "text",
      sortable: false,
      accessor: (row: any) => row.grn_references?.[0]?.po_number || "-",
    },
    {
      id: "grand_total",
      header: "Amount",
      field: "grand_total",
      type: "money",
      sortable: true,
    },
    {
      id: "status",
      header: "Status",
      field: "status",
      type: "status",
      statusConfig: invoiceStatusConfig,
      sortable: true,
    },
    {
      id: "invoice_date",
      header: "Invoice Date",
      field: "invoice_date",
      type: "datetime",
      dateFormat: "relative",
      sortable: true,
    },
    {
      id: "actions",
      header: "Actions",
      field: "actions",
      type: "action",
      renderAction: (row: WithId & Record<string, any>) => (
        <>
          <IconButton
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              setAnchorEl(event.currentTarget);
              setMenuInvoiceId(row.invoice_id);
            }}
          >
            <Icon>more_vert</Icon>
          </IconButton>
          {menuInvoiceId === row.invoice_id && (
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
            >
              <MenuItem
                onClick={(event) => {
                  event.stopPropagation();
                  handleViewInvoice(row as unknown as PurchaseInvoice);
                }}
                disabled={
                  !hasPermission(
                    "inventory.invoices.view",
                    user?.newPermissions,
                    user?.assignment_details?.id,
                    user?.is_store_owner
                  )
                }
              >
                <Icon>visibility</Icon> View Details
              </MenuItem>
              <MenuItem
                onClick={(event) => {
                  event.stopPropagation();
                  handleEditInvoice(row as unknown as PurchaseInvoice);
                }}
                disabled={
                  !hasPermission(
                    "inventory.invoices.edit",
                    user?.newPermissions,
                    user?.assignment_details?.id,
                    user?.is_store_owner
                  )
                }
              >
                <Icon>edit</Icon> Edit
              </MenuItem>
              <Divider />
              {row.status === "draft" && (
                <MenuItem
                  onClick={(event) => {
                    event.stopPropagation();
                    handleStatusChange(
                      row as unknown as PurchaseInvoice,
                      "pending_approval" as InvoiceStatus
                    );
                  }}
                  disabled={
                    !hasPermission(
                      "inventory.invoices.edit",
                      user?.newPermissions,
                      user?.assignment_details?.id,
                      user?.is_store_owner
                    )
                  }
                >
                  <Icon>send</Icon> Submit for Approval
                </MenuItem>
              )}
              {row.status === "pending_approval" && (
                <MenuItem
                  onClick={(event) => {
                    event.stopPropagation();
                    handleStatusChange(
                      row as unknown as PurchaseInvoice,
                      "approved" as InvoiceStatus
                    );
                  }}
                  disabled={
                    !hasPermission(
                      "inventory.invoices.edit",
                      user?.newPermissions,
                      user?.assignment_details?.id,
                      user?.is_store_owner
                    )
                  }
                >
                  <Icon>check_circle</Icon> Approve
                </MenuItem>
              )}
              {row.status === "approved" && (
                <MenuItem
                  onClick={(event) => {
                    event.stopPropagation();
                    handleStatusChange(
                      row as unknown as PurchaseInvoice,
                      "paid" as InvoiceStatus
                    );
                  }}
                  disabled={
                    !hasPermission(
                      "inventory.invoices.edit",
                      user?.newPermissions,
                      user?.assignment_details?.id,
                      user?.is_store_owner
                    )
                  }
                >
                  <Icon>payments</Icon> Mark as Paid
                </MenuItem>
              )}
              <Divider />
              <MenuItem>
                <Icon>print</Icon> Print Invoice
              </MenuItem>
              <MenuItem>
                <Icon>download</Icon> Download PDF
              </MenuItem>
            </Menu>
          )}
        </>
      ),
    },
  ];

  // Filter groups for the search component
  const filterGroups: FilterGroup[] = [
    {
      id: "status",
      label: "Status",
      type: "select",
      options: Object.entries(invoiceStatusConfig).map(([value, config]) => ({
        id: value,
        label: config.label,
        value,
      })),
    },
    {
      id: "dateRange",
      label: "Date Range",
      type: "daterange",
    },
  ];

  // Load invoices
  const loadInvoices = async () => {
    setLoading(true);
    try {
      const params: ListInvoicesRequest = {
        business_id: getBusinessIdForList(),
        params: {
          page: page + 1, // API uses 1-based pagination
          limit: pageSize,
          sort_by: sortField,
          sort_order: sortOrder,
          search: search || undefined,
          status: filters.status ? [filters.status] : undefined,
          date_from: filters.startDate || undefined,
          date_to: filters.endDate || undefined,
        },
      };

      console.log("[DEBUG] InvoiceList request params:", params);

      const response = await invoiceService.listInvoices(params);
      console.log("[DEBUG] InvoiceList response:", response);

      // Support both old and new API response shapes
      const invoicesArray = response.data.invoices || response.data.data || [];
      const totalCount =
        response.data.pagination?.total_count ||
        response.data.metadata?.total?.value ||
        0;

      if (response.success && response.data) {
        setInvoices(invoicesArray);
        setTotalItems(totalCount);
      } else {
        setInvoices([]);
        setTotalItems(0);
      }
    } catch (error: any) {
      console.error("Error loading invoices:", error);
      toast.error(error.message || "Failed to load invoices");
      setInvoices([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  // Handle row click
  const handleRowClick = (row: WithId & Record<string, any>) => {
    navigate(`/business/procurement/invoices/${row.invoice_id}`);
  };

  // Handle menu close
  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuInvoiceId(null);
  };

  // Handle create invoice
  const handleCreateInvoice = () => {
    navigate("/business/procurement/invoices/new");
  };

  // Handle edit invoice
  const handleEditInvoice = (invoice: PurchaseInvoice) => {
    navigate(`/business/procurement/invoices/${invoice.invoice_id}/edit`);
  };

  // Handle view invoice
  const handleViewInvoice = (invoice: PurchaseInvoice) => {
    navigate(`/business/procurement/invoices/${invoice.invoice_id}`);
  };

  // Format currency
  const formatCurrency = (amount: number, currency = "USD") => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
    }).format(amount);
  };

  // Status transition handler
  const handleStatusChange = async (
    invoice: PurchaseInvoice,
    newStatus: InvoiceStatus
  ) => {
    try {
      await invoiceService.updateInvoice({
        business_id: getBusinessIdForList(),
        invoice_id: invoice.invoice_id,
        update_data: { status: newStatus },
      });
      toast.success(`Invoice status updated to ${newStatus.replace("_", " ")}`);
      // If marking as paid, update GRN and PO statuses
      if (newStatus === "paid") {
        const business_id = getBusinessIdForList();
        // Mark all related GRNs as invoiced
        await Promise.all(
          (invoice.grn_references || []).map(async (grn) => {
            // Mark GRN as invoiced
            if (grn.grn_id) {
              await grnService.markAsInvoiced({
                _id: grn.grn_id,
                invoice_reference: invoice.invoice_id,
                invoice_date: invoice.invoice_date,
              });
              toast.success(
                `GRN ${grn.grn_number || grn.grn_id} marked as Invoiced`
              );
            }
            // Close related PO if po_id exists
            if (grn.po_id) {
              await invoiceService.closePurchaseOrder({
                _id: grn.po_id,
              });
              toast.success(`PO ${grn.po_number || grn.po_id} closed`);
            }
          })
        );
      }
      loadInvoices(); // Refresh the list
    } catch (error) {
      toast.error("Failed to update invoice status");
    }
  };

  // Load data on component mount and when dependencies change
  useEffect(() => {
    loadInvoices();
  }, [page, pageSize, search, filters, sortField, sortOrder]);

  const selectedMenuInvoice = invoices?.find(
    (invoice) => invoice.invoice_id === menuInvoiceId
  );

  return (
    <Fragment>
      <PageHeader
        title="Purchase Invoices"
        subtitle="Manage purchase invoices created from approved GRNs • Click any row to view details"
        icon="receipt"
        actions={
          <Button
            variant="contained"
            startIcon={<Icon>add</Icon>}
            onClick={handleCreateInvoice}
            disabled={
              !hasPermission(
                "inventory.invoices.create",
                user?.newPermissions,
                user?.assignment_details?.id,
                user?.is_store_owner
              )
            }
          >
            Create Invoice
          </Button>
        }
      />

      <Box sx={{ mb: 3 }}>
        <AppSearch
          onSearch={setSearch}
          onFilterChange={setFilters}
          filterGroups={filterGroups}
          placeholder="Search invoices by number, vendor, GRN..."
        />
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="primary">
                {invoices?.filter((inv) => inv.status === "draft").length || 0}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Draft Invoices
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="warning.main">
                {invoices?.filter((inv) => inv.status === "pending_approval")
                  .length || 0}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Pending Approval
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="success.main">
                {invoices?.filter((inv) => inv.status === "approved").length ||
                  0}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Approved
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="error.main">
                {invoices?.filter((inv) => inv.status === "paid").length || 0}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Paid
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {loading || (invoices && invoices.length > 0) ? (
        <AppTable
          columns={columns}
          data={invoices}
          loading={loading}
          pagination={{
            total: totalItems,
            page,
            pageSize,
            onPageChange: setPage,
            onPageSizeChange: setPageSize,
          }}
          onSort={(field: string, direction: "asc" | "desc") => {
            setSortField(field);
            setSortOrder(direction);
          }}
          onRowClick={handleRowClick}
          toolbarActions={{
            export: {
              label: "Export",
              is_disabled: !invoices || invoices.length === 0,
            },
          }}
        />
      ) : (
        <EmptyState
          title="No Purchase Invoices"
          description="Create your first invoice from an approved GRN to get started"
          icon="receipt"
          actionText="Create Invoice"
          onAction={handleCreateInvoice}
        />
      )}
    </Fragment>
  );
};
